import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowsClockwise, Trash } from '@phosphor-icons/react';
import { api } from '../../lib/api';
import { SkeletonAdminDetail } from '../../components/Skeleton';
import { useConfirm } from '../../components/ConfirmModal';

function AddMemberModal({ clinicId, existingMemberIds, onClose, onAdded }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);

  useEffect(() => {
    api.admin.users()
      .then((data) => {
        setUsers(data.filter(u => !existingMemberIds.includes(u.id)));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [existingMemberIds]);

  const handleAdd = async (userId) => {
    setAdding(userId);
    try {
      await api.admin.addClinicMember(clinicId, userId);
      onAdded();
      onClose();
    } catch (err) {
      console.error('Add member failed:', err);
    }
    setAdding(null);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-text mb-4">Add Member</h3>
        {loading ? (
          <p className="text-sm text-gray-500">Loading users...</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-gray-500">All users are already members of this clinic.</p>
        ) : (
          <div className="overflow-y-auto flex-1 -mx-2">
            {users.map((user) => (
              <div key={user.id} className="flex items-center justify-between px-2 py-2.5 hover:bg-gray-50 rounded-lg">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text truncate">{user.first_name} {user.last_name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                <button
                  onClick={() => handleAdd(user.id)}
                  disabled={adding === user.id}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-80 disabled:opacity-50 flex-shrink-0 ml-3"
                >
                  {adding === user.id ? 'Adding...' : 'Add'}
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end mt-4 pt-4 border-t border-[#E6E6E6]">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminClinicDetail() {
  const { clinicId } = useParams();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sftpFolder, setSftpFolder] = useState('');
  const [savingSftp, setSavingSftp] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const { confirm, confirmModal } = useConfirm();

  const loadClinic = () => {
    api.admin.clinic(clinicId)
      .then((data) => {
        setClinic(data);
        setSftpFolder(data.sftp_folder_name || '');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadClinic();
  }, [clinicId]);

  const handleSaveSftp = async () => {
    setSavingSftp(true);
    try {
      await api.admin.updateClinic(clinicId, { sftp_folder_name: sftpFolder || null });
    } catch (err) {
      console.error('Save SFTP folder failed:', err);
    }
    setSavingSftp(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await api.hipaaVault.sync(clinicId);
      // Daily caseload runs second, mirroring the scheduled job, so the
      // month-to-date average is the last thing written to the Caseload column.
      // Its failure shouldn't discard a successful discharge sync.
      let caseload = null;
      try {
        caseload = await api.dailyCaseload.sync(clinicId);
      } catch (err) {
        caseload = { error: err.message };
      }
      let attendance = null;
      try {
        attendance = await api.dailyAttendance.sync(clinicId);
      } catch (err) {
        attendance = { error: err.message };
      }
      setSyncResult({ ...result, caseload, attendance });
      loadClinic();
    } catch (err) {
      setSyncResult({ error: err.message });
    }
    setSyncing(false);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await api.hipaaVault.testConnection(clinicId);
      setTestResult(result);
    } catch (err) {
      setTestResult({ error: err.message });
    }
    setTesting(false);
  };

  const handleRemoveMember = async (member) => {
    const fullName = `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email;
    const ok = await confirm({
      title: 'Remove member?',
      message: `${fullName} will lose access to ${clinic?.name || 'this clinic'}.`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.admin.removeClinicMember(clinicId, member.id);
      loadClinic();
    } catch (err) {
      console.error('Remove member failed:', err);
    }
  };

  if (loading) return <SkeletonAdminDetail />;
  if (!clinic) return <p className="text-sm text-gray-500">Clinic not found.</p>;

  const lastSync = clinic.last_hipaa_sync;

  return (
    <div>
      {/* Back link */}
      <button
        onClick={() => navigate('/admin/clinics')}
        className="flex items-center gap-1.5 text-sm text-primary font-medium mb-4 hover:underline"
      >
        <ArrowLeft size={16} /> Back
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <h1 className="text-2xl md:text-[32px] font-semibold text-text">{clinic.name}</h1>
        <div className="flex-shrink-0 text-right">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <ArrowsClockwise size={16} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing...' : 'Sync w/ Hippa Vault'}
          </button>
          {lastSync && (
            <p className="text-xs text-gray-500 mt-1">
              Last scheduled: {lastSync.scheduled_date} for {lastSync.date}
            </p>
          )}
          {syncResult && (
            <div className={`mt-2 text-xs text-left p-2 rounded-lg ${syncResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {syncResult.error
                ? syncResult.error
                : `Synced ${syncResult.fileName}: ${syncResult.processed} processed, ${syncResult.skipped} skipped`}
              {syncResult.caseload && (
                <div className={`mt-1 pt-1 border-t ${syncResult.caseload.error ? 'text-red-700 border-red-200' : 'border-green-200'}`}>
                  {syncResult.caseload.error
                    ? `Daily caseload: ${syncResult.caseload.error}`
                    : syncResult.caseload.filesIngested
                      ? `Daily caseload: ${syncResult.caseload.filesIngested} day${syncResult.caseload.filesIngested === 1 ? '' : 's'} ingested through ${syncResult.caseload.latestDate}, ${syncResult.caseload.recordsUpdated} caseload${syncResult.caseload.recordsUpdated === 1 ? '' : 's'} updated`
                      : 'Daily caseload: no new report days'}
                </div>
              )}
              {syncResult.attendance && (
                <div className={`mt-1 pt-1 border-t ${syncResult.attendance.error ? 'text-red-700 border-red-200' : 'border-green-200'}`}>
                  {syncResult.attendance.error
                    ? `Daily attendance: ${syncResult.attendance.error}`
                    : syncResult.attendance.filesIngested
                      ? `Daily attendance: ${syncResult.attendance.filesIngested} day${syncResult.attendance.filesIngested === 1 ? '' : 's'} ingested through ${syncResult.attendance.latestDate}, ${syncResult.attendance.recordsUpdated} attendance${syncResult.attendance.recordsUpdated === 1 ? '' : 's'} updated`
                      : 'Daily attendance: no new report days'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* SFTP Folder */}
      <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mb-6">
        <p className="text-sm font-bold text-text mb-3">SFTP Folder Name</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={sftpFolder}
            onChange={(e) => setSftpFolder(e.target.value)}
            onBlur={handleSaveSftp}
            placeholder="e.g. topsail"
            className="flex-1 border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
          />
          <button
            onClick={handleTestConnection}
            disabled={testing}
            className="px-4 py-2 text-sm font-medium text-primary border border-primary-30 rounded-lg hover:bg-[#F5E9FD] transition-colors flex items-center gap-2 flex-shrink-0 disabled:opacity-50"
          >
            <ArrowsClockwise size={16} className={testing ? 'animate-spin' : ''} />
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
        </div>
        {testResult && (
          <div className={`mt-3 text-xs p-3 rounded-lg ${testResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {testResult.error
              ? testResult.error
              : `Connected to ${testResult.folder} — ${testResult.fileCount} file(s) found`}
          </div>
        )}
      </div>

      {/* Members */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-text">Members</h2>
        <button
          onClick={() => setAddMemberOpen(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 transition-colors flex items-center gap-1"
        >
          <span className="text-lg leading-none">+</span> Add Members
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#E6E6E6]">
        {clinic.members.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-500">No members</div>
        ) : (
          clinic.members.map((member, idx) => (
            <div
              key={member.id}
              className={`flex items-center justify-between px-4 md:px-5 py-3.5 ${
                idx !== clinic.members.length - 1 ? 'border-b border-[#E6E6E6]' : ''
              }`}
            >
              <p className="text-sm font-medium text-text">
                {member.first_name} {member.last_name}
              </p>
              <button
                onClick={() => handleRemoveMember(member)}
                className="text-gray-400 hover:text-destructive transition-colors p-1"
                title="Remove member"
              >
                <Trash size={18} />
              </button>
            </div>
          ))
        )}
      </div>

      {addMemberOpen && (
        <AddMemberModal
          clinicId={clinicId}
          existingMemberIds={clinic.members.map(m => m.id)}
          onClose={() => setAddMemberOpen(false)}
          onAdded={loadClinic}
        />
      )}
      {confirmModal}
    </div>
  );
}
